import { html, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";

/** One-line preview for a collapsed prompt (strip heading markers). */
export function promptCollapsedPreview(text: string, maxLen = 100): string {
  const body = text.trim();
  if (!body) {
    return "";
  }
  const line =
    body
      .split(/\n/)
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? body;
  const cleaned = line.replace(/^#{1,6}\s+/, "").replace(/^[-*]\s+/, "");
  if (cleaned.length <= maxLen) {
    return cleaned;
  }
  return `${cleaned.slice(0, maxLen - 1)}…`;
}

export type CollapsibleMarkdownPromptProps = {
  text: string;
  /** Summary label when expanded / always shown. */
  title?: string;
  /** Open by default (default: false — keep transcripts scannable). */
  defaultOpen?: boolean;
  /** Extra class on the details root. */
  className?: string;
};

/**
 * Collapsed-by-default markdown prompt block for subagent task text.
 * Prevents raw `#` / `##` walls of text from dominating the transcript.
 */
export function renderCollapsibleMarkdownPrompt(props: CollapsibleMarkdownPromptProps) {
  const body = props.text.trim();
  if (!body) {
    return nothing;
  }
  const open = props.defaultOpen === true;
  const preview = promptCollapsedPreview(body);
  const title = props.title?.trim() || "Prompt";
  const rootClass = ["chat-prompt-collapse", props.className].filter(Boolean).join(" ");

  return html`
    <details class=${rootClass} ?open=${open}>
      <summary class="chat-prompt-collapse__summary">
        <span class="chat-prompt-collapse__chevron" aria-hidden="true">▸</span>
        <span class="chat-prompt-collapse__title">${title}</span>
        ${
          !open && preview
            ? html`<span class="chat-prompt-collapse__preview muted">${preview}</span>`
            : nothing
        }
      </summary>
      <div class="chat-prompt-collapse__body">
        <div class="chat-prompt-collapse__content chat-text">
          ${unsafeHTML(toSanitizedMarkdownHtml(body))}
        </div>
      </div>
    </details>
  `;
}
